import type {
  ResolvedThemeToken,
  ThemeColorValue,
  ThemeDimensionValue,
  ThemeDurationValue,
  ThemeTokenExtensions,
  ThemeTokens,
} from '@inf-monkeys-tech/monkeys/contracts';
import { resolveThemeTokens } from '@inf-monkeys-tech/monkeys/runtime';

export type MonkeysThemeColorMode = 'light' | 'dark' | (string & {});
export type MonkeysThemeDensity = 'compact' | 'default' | 'comfortable';

export interface CompileThemeTokensOptions {
  colorMode?: MonkeysThemeColorMode;
  density?: MonkeysThemeDensity;
  variablePrefix?: string;
}

export interface CompiledThemeTokens {
  contract: ThemeTokens;
  colorMode: MonkeysThemeColorMode;
  density: MonkeysThemeDensity;
  values: Readonly<Record<string, unknown>>;
  cssValues: Readonly<Record<string, string>>;
  cssVariables: Readonly<Record<string, string>>;
  cssText: string;
}

export interface ApplyThemeTokensOptions extends CompileThemeTokensOptions {
  target?: HTMLElement | null;
  themeId?: string;
}

export interface ClearThemeTokensOptions {
  target?: HTMLElement | null;
}

type CssExtension = {
  gradient?: {
    kind?: 'linear' | 'radial';
    angle?: number;
  };
};

const CSS_EXTENSION_KEY = 'com.infmonkeys.css';
const APPLIED_VARIABLES = new WeakMap<object, Set<string>>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function readCssExtension(extensions?: ThemeTokenExtensions): CssExtension | undefined {
  const extension = extensions?.[CSS_EXTENSION_KEY];
  return isRecord(extension) ? extension as CssExtension : undefined;
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(6)));
}

function formatDimension(value: ThemeDimensionValue): string {
  return `${formatNumber(value.value)}${value.unit}`;
}

function formatDuration(value: ThemeDurationValue): string {
  return `${formatNumber(value.value)}${value.unit}`;
}

function formatColorComponent(value: number | 'none'): string {
  return value === 'none' ? value : formatNumber(value);
}

function hexToSrgb(hex: string): [number, number, number] {
  return [
    Number.parseInt(hex.slice(1, 3), 16) / 255,
    Number.parseInt(hex.slice(3, 5), 16) / 255,
    Number.parseInt(hex.slice(5, 7), 16) / 255,
  ];
}

function colorSrgbComponents(value: ThemeColorValue): [number, number, number] {
  if (value.colorSpace === 'srgb' && value.components.every((component) => typeof component === 'number')) {
    return value.components as [number, number, number];
  }
  if (value.hex) return hexToSrgb(value.hex);
  throw new TypeError(
    `Web semantic colors in ${value.colorSpace} require a six-digit hex fallback for hsl(var(--token)) consumers.`,
  );
}

function srgbToHsl([red, green, blue]: [number, number, number]): [number, number, number] {
  const maximum = Math.max(red, green, blue);
  const minimum = Math.min(red, green, blue);
  const lightness = (maximum + minimum) / 2;
  if (maximum === minimum) return [0, 0, lightness * 100];
  const delta = maximum - minimum;
  const saturation = lightness > 0.5
    ? delta / (2 - maximum - minimum)
    : delta / (maximum + minimum);
  let hue: number;
  if (maximum === red) hue = (green - blue) / delta + (green < blue ? 6 : 0);
  else if (maximum === green) hue = (blue - red) / delta + 2;
  else hue = (red - green) / delta + 4;
  return [(hue / 6) * 360, saturation * 100, lightness * 100];
}

function formatHslTriplet(value: ThemeColorValue): string {
  if (value.colorSpace === 'hsl' && value.components.every((component) => typeof component === 'number')) {
    const [hue, saturation, lightness] = value.components as [number, number, number];
    return `${formatNumber(hue)} ${formatNumber(saturation)}% ${formatNumber(lightness)}%`;
  }
  const [hue, saturation, lightness] = srgbToHsl(colorSrgbComponents(value));
  return `${formatNumber(hue)} ${formatNumber(saturation)}% ${formatNumber(lightness)}%`;
}

function formatColor(value: ThemeColorValue): string {
  const alpha = value.alpha ?? 1;
  if (value.hex && alpha === 1) return value.hex;
  const components = value.components.map(formatColorComponent).join(' ');
  const alphaSuffix = alpha === 1 ? '' : ` / ${formatNumber(alpha)}`;
  switch (value.colorSpace) {
    case 'srgb': {
      const rgb = value.components.map((component) => component === 'none' ? 'none' : formatNumber(component * 255));
      return `rgb(${rgb.join(' ')}${alphaSuffix})`;
    }
    case 'hsl':
      return `hsl(${formatColorComponent(value.components[0])} ${formatColorComponent(value.components[1])}% ${formatColorComponent(value.components[2])}%${alphaSuffix})`;
    case 'hwb':
      return `hwb(${formatColorComponent(value.components[0])} ${formatColorComponent(value.components[1])}% ${formatColorComponent(value.components[2])}%${alphaSuffix})`;
    case 'lab':
    case 'lch':
    case 'oklab':
    case 'oklch':
      return `${value.colorSpace}(${components}${alphaSuffix})`;
    default:
      return `color(${value.colorSpace} ${components}${alphaSuffix})`;
  }
}

function formatFontFamily(value: string | string[]): string {
  const families = Array.isArray(value) ? value : [value];
  const genericFamilies = new Set([
    'serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'system-ui',
    'ui-serif', 'ui-sans-serif', 'ui-monospace', 'ui-rounded', 'math', 'emoji', 'fangsong',
  ]);
  return families.map((family) => {
    if (genericFamilies.has(family) || /^[a-zA-Z][\w-]*$/.test(family)) return family;
    return `"${family.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  }).join(', ');
}

function formatShadow(value: unknown): string {
  const shadows = Array.isArray(value) ? value : [value];
  return shadows.map((shadow) => {
    if (!isRecord(shadow)) throw new TypeError('DTCG shadow values must be objects.');
    return [
      formatDimension(shadow.offsetX as ThemeDimensionValue),
      formatDimension(shadow.offsetY as ThemeDimensionValue),
      formatDimension(shadow.blur as ThemeDimensionValue),
      formatDimension(shadow.spread as ThemeDimensionValue),
      formatColor(shadow.color as ThemeColorValue),
      shadow.inset === true ? 'inset' : '',
    ].filter(Boolean).join(' ');
  }).join(', ');
}

function formatGradient(value: unknown, extensions?: ThemeTokenExtensions): string {
  if (!Array.isArray(value)) throw new TypeError('DTCG gradient values must be arrays.');
  const stops = value.map((stop) => {
    if (!isRecord(stop)) throw new TypeError('DTCG gradient stops must be objects.');
    return `${formatColor(stop.color as ThemeColorValue)} ${formatNumber(Number(stop.position) * 100)}%`;
  }).join(', ');
  const gradient = readCssExtension(extensions)?.gradient;
  if (gradient?.kind === 'radial') return `radial-gradient(${stops})`;
  return `linear-gradient(${formatNumber(gradient?.angle ?? 90)}deg, ${stops})`;
}

function formatTokenValue(token: ResolvedThemeToken): string {
  switch (token.type) {
    case 'color':
      return formatColor(token.value as ThemeColorValue);
    case 'dimension':
      return formatDimension(token.value as ThemeDimensionValue);
    case 'fontFamily':
      return formatFontFamily(token.value as string | string[]);
    case 'fontWeight':
    case 'number':
      return String(token.value);
    case 'duration':
      return formatDuration(token.value as ThemeDurationValue);
    case 'cubicBezier':
      return `cubic-bezier(${(token.value as number[]).map(formatNumber).join(', ')})`;
    case 'strokeStyle': {
      if (typeof token.value === 'string') return token.value;
      const value = token.value as { dashArray: ThemeDimensionValue[] };
      return value.dashArray.map(formatDimension).join(' ');
    }
    case 'border': {
      const value = token.value as { color: ThemeColorValue; width: ThemeDimensionValue; style: unknown };
      const styleToken: ResolvedThemeToken = { ...token, type: 'strokeStyle', value: value.style };
      return `${formatDimension(value.width)} ${formatTokenValue(styleToken)} ${formatColor(value.color)}`;
    }
    case 'transition': {
      const value = token.value as { duration: ThemeDurationValue; delay: ThemeDurationValue; timingFunction: number[] };
      return `${formatDuration(value.duration)} cubic-bezier(${value.timingFunction.map(formatNumber).join(', ')}) ${formatDuration(value.delay)}`;
    }
    case 'shadow':
      return formatShadow(token.value);
    case 'gradient':
      return formatGradient(token.value, token.extensions);
    case 'typography': {
      const value = token.value as {
        fontFamily: string | string[];
        fontSize: ThemeDimensionValue;
        fontWeight: string | number;
        lineHeight: number;
      };
      return `${value.fontWeight} ${formatDimension(value.fontSize)}/${formatNumber(value.lineHeight)} ${formatFontFamily(value.fontFamily)}`;
    }
  }
}

function toCssVariableName(path: string, prefix: string): string {
  const normalized = path
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return `--${prefix}-${normalized}`;
}

function assertCssVariablePrefix(prefix: string): void {
  if (!/^[a-zA-Z][a-zA-Z0-9-]*$/.test(prefix)) {
    throw new TypeError(
      'Theme token variablePrefix must start with a letter and contain only letters, numbers, or hyphens.',
    );
  }
}

function selectModePath(
  paths: ReadonlySet<string>,
  basePath: string,
  colorMode: MonkeysThemeColorMode,
): string | undefined {
  const contextualPath = `${basePath}.${colorMode}`;
  if (paths.has(contextualPath)) return contextualPath;
  return paths.has(basePath) ? basePath : undefined;
}

const HSL_SEMANTIC_ALIASES: Readonly<Record<string, string>> = {
  'semantic.color.background': '--background',
  'semantic.color.foreground': '--foreground',
  'semantic.color.card': '--card',
  'semantic.color.card-foreground': '--card-foreground',
  'semantic.color.popover': '--popover',
  'semantic.color.popover-foreground': '--popover-foreground',
  'semantic.color.primary': '--primary',
  'semantic.color.primary-foreground': '--primary-foreground',
  'semantic.color.secondary': '--secondary',
  'semantic.color.secondary-foreground': '--secondary-foreground',
  'semantic.color.muted': '--muted',
  'semantic.color.muted-foreground': '--muted-foreground',
  'semantic.color.accent': '--accent',
  'semantic.color.accent-foreground': '--accent-foreground',
  'semantic.color.destructive': '--destructive',
  'semantic.color.destructive-foreground': '--destructive-foreground',
  'semantic.color.success': '--success',
  'semantic.color.success-foreground': '--success-foreground',
  'semantic.color.warning': '--warning',
  'semantic.color.warning-foreground': '--warning-foreground',
  'semantic.color.border': '--border',
  'semantic.color.input': '--input',
  'semantic.color.ring': '--ring',
  'semantic.color.media-surface': '--media-surface',
  'semantic.color.media-foreground': '--media-foreground',
  'semantic.color.media-overlay': '--media-overlay',
  'semantic.color.radar-canvas': '--radar-canvas',
  'semantic.color.radar-panel': '--radar-panel',
  'semantic.color.radar-border': '--radar-border',
  'semantic.color.radar-shell-border': '--radar-shell-border',
  'semantic.color.radar-grid': '--radar-grid',
  'semantic.color.radar-axis': '--radar-axis',
  'semantic.color.radar-series-one': '--radar-series-one',
  'semantic.color.radar-series-two': '--radar-series-two',
  'semantic.color.radar-series-three': '--radar-series-three',
  'semantic.color.radar-series-four': '--radar-series-four',
};

export function getThemeTokenCssValue(
  compiled: CompiledThemeTokens,
  basePath: string,
): string | undefined {
  const path = selectModePath(new Set(Object.keys(compiled.cssValues)), basePath, compiled.colorMode);
  return path ? compiled.cssValues[path] : undefined;
}

export function compileThemeTokens(
  input: ThemeTokens,
  options: CompileThemeTokensOptions = {},
): CompiledThemeTokens {
  const resolved = resolveThemeTokens(input);
  const contract = resolved.document;
  const colorMode = options.colorMode ?? 'light';
  const density = options.density ?? 'default';
  const variablePrefix = options.variablePrefix ?? 'monkeys';
  assertCssVariablePrefix(variablePrefix);
  const values: Record<string, unknown> = {};
  const cssValues: Record<string, string> = {};
  const cssVariables: Record<string, string> = {};
  const canonicalVariableSources = new Map<string, string>();

  for (const [path, token] of resolved.tokens) {
    values[path] = token.value;
    const cssValue = formatTokenValue(token);
    cssValues[path] = cssValue;
    const variableName = toCssVariableName(path, variablePrefix);
    const existingPath = canonicalVariableSources.get(variableName);
    if (existingPath) {
      throw new TypeError(
        `Theme token paths ${existingPath} and ${path} both compile to ${variableName}. Rename one token.`,
      );
    }
    canonicalVariableSources.set(variableName, path);
    cssVariables[variableName] = cssValue;
  }

  const paths = new Set(resolved.tokens.keys());
  for (const [basePath, variableName] of Object.entries(HSL_SEMANTIC_ALIASES)) {
    const path = selectModePath(paths, basePath, colorMode);
    if (!path) continue;
    const token = resolved.tokens.get(path);
    if (!token || token.type !== 'color') {
      throw new TypeError(`${path} must be a color token to drive ${variableName}.`);
    }
    cssVariables[variableName] = formatHslTriplet(token.value as ThemeColorValue);
  }

  const directAliases: Array<[
    string,
    string,
    MonkeysThemeColorMode | MonkeysThemeDensity,
    ResolvedThemeToken['type'],
  ]> = [
    ['semantic.color.neocard', '--neocard', colorMode, 'color'],
    ['semantic.radius.base', '--radius', colorMode, 'dimension'],
    ['semantic.spacing.global', '--global-spacing', density, 'dimension'],
    ['semantic.gradient.brand', '--monkeys-gradient', colorMode, 'gradient'],
    ['semantic.font.body', '--font-sans', colorMode, 'fontFamily'],
  ];
  for (const [basePath, variableName, axis, expectedType] of directAliases) {
    const path = selectModePath(paths, basePath, axis);
    if (!path) continue;
    const token = resolved.tokens.get(path);
    if (!token || token.type !== expectedType) {
      throw new TypeError(`${path} must be a ${expectedType} token to drive ${variableName}.`);
    }
    cssVariables[variableName] = cssValues[path];
  }

  const cssText = Object.entries(cssVariables)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([name, value]) => `${name}:${value};`)
    .join('');

  return {
    contract,
    colorMode,
    density,
    values: Object.freeze(values),
    cssValues: Object.freeze(cssValues),
    cssVariables: Object.freeze(cssVariables),
    cssText,
  };
}

export function applyThemeTokens(
  input: ThemeTokens,
  options: ApplyThemeTokensOptions = {},
): CompiledThemeTokens {
  const compiled = compileThemeTokens(input, options);
  const target = options.target ?? (typeof document === 'undefined' ? null : document.documentElement);
  if (!target) return compiled;

  const previouslyApplied = APPLIED_VARIABLES.get(target) ?? new Set<string>();
  const nextVariables = new Set(Object.keys(compiled.cssVariables));
  for (const name of previouslyApplied) {
    if (!nextVariables.has(name)) target.style.removeProperty(name);
  }
  for (const [name, value] of Object.entries(compiled.cssVariables)) {
    target.style.setProperty(name, value);
  }
  APPLIED_VARIABLES.set(target, nextVariables);

  if (options.themeId) target.dataset.monkeysTheme = options.themeId;
  else delete target.dataset.monkeysTheme;
  target.dataset.monkeysColorMode = compiled.colorMode;
  target.dataset.monkeysDensity = compiled.density;
  return compiled;
}

export function clearThemeTokens(options: ClearThemeTokensOptions = {}): void {
  const target = options.target ?? (typeof document === 'undefined' ? null : document.documentElement);
  if (!target) return;

  const previouslyApplied = APPLIED_VARIABLES.get(target) ?? new Set<string>();
  for (const name of previouslyApplied) target.style.removeProperty(name);
  APPLIED_VARIABLES.delete(target);

  delete target.dataset.monkeysTheme;
  delete target.dataset.monkeysColorMode;
  delete target.dataset.monkeysDensity;
}
